import axiosInstance from 'config/axiosInstance';
import { FC, FormEvent, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSaveError } from 'reusable/useSaveError';
import { useModalDialog } from 'reusable/useModalDialog';

interface DeleteClientDialogProps {
  id: string;
  name: string;
  isOpen: boolean;
  onClose: () => void;
  fields: DeleteClientDialogField[];
}

interface DeleteClientDialogField {
  label: string;
  value: string;
}

const DeleteClientDialog: FC<DeleteClientDialogProps> = ({
  id,
  name,
  isOpen,
  onClose,
  fields,
}) => {
  const { t } = useTranslation('client');
  const { showSaveError, saveErrorPopup } = useSaveError();
  // ADM-16。Escape で閉じる・フォーカスを中に留める・閉じたら開いた操作へ戻す。
  // RV-125。見出しの id は文書の中で一意にする（重なったダイアログが別の見出しで読まれた）。
  const headingId = useId();
  const dialogRef = useModalDialog(isOpen, onClose);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await axiosInstance.delete(`v1/client_deliveries/${id}/`);
      onClose();
      navigate(`/client`);
      window.location.reload();
    } catch (error) {
      // EV-320。失敗を利用者に伝える（以前は console だけで、何も表示されなかった）。ダイアログと対象は残す。
      console.error('Error deleting client:', error);
      showSaveError(error, t('ui.delete-failed', { ns: 'common' }));
    }
  };

  const handleCancelOrClose = () => {
    onClose();
  };

  return (
    <div className="fixed z-20 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
          ref={dialogRef}
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/500"
                    id={headingId}
                  >
                    {t('delete.title')}
                  </h3>
                  <button type="button"
                    onClick={handleCancelOrClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <span className="sr-only">{t('ui.close', { ns: 'common' })}</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-neutral/50 mt-4 w-full" />
                <p className="m-4 sm:mx-6 sm:mt-6">
                  {t('delete.subtitle_1')}{' '}
                  <span className="text-primary/300 text-semibold text-subtitle-2">
                    {name}
                  </span>{' '}
                  {t('delete.subtitle_2')}
                </p>
                <form onSubmit={handleSubmit}>
                  <div
                    className={`grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-4 m-4 sm:m-6 sm:rounded-lg border sm:border-neutral/50 sm:p-4`}
                  >
                    {fields.map((field, index) => (
                      <div key={index}>
                        <label className="block mb-1 text-overline font-regular text-neutral/100">
                          {field.label}
                        </label>
                        <p className="text-body-2 font-normal text-neutral/300 break-words">
                          {field.value}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md border bg-white text-subtitle-2 text-semibold text-neutral/75"
                      onClick={handleCancelOrClose}
                    >
                      {t('action.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="w-full inline-flex justify-center items-center rounded-md bg-error text-subtitle-2 text-white h-12"
                    >
                      {t('action.delete')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      {saveErrorPopup}
    </div>
  );
};

export default DeleteClientDialog;
